/**
 * File format detection and utilities
 * Extracted from web-app for reusability across integrations
 */

/**
 * Image file extensions that support presets/transformations
 */
export const PRESET_SUPPORTED_FORMATS = [
  'gif',
  'png',
  'jpg',
  'jp2',
  'jps',
  'jpeg',
  'bmp',
  'tiff',
  'tif',
  'svg',
  'eps',
  'ai',
  'ico',
  'psd',
  'arw',
  'crw',
  'nrw',
  'cr2',
  'dcr',
  'dng',
  'mrw',
  'nef',
  'orf',
  'pef',
  'raf',
  'indt',
  'indd',
  'idml',
  'webp',
  'pam',
  'pbm',
  'xbm',
  '3fr',
  'mos',
  'ppm',
  'erf',
  'x3f',
  'kdc',
  'sr2',
  'mef',
] as const;

/**
 * Image formats that support drag-and-drop embedding
 */
export const DRAG_DROP_SUPPORTED_FORMATS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'svg',
  'avif',
  'apng',
  'bmp',
  'ico',
] as const;

/**
 * Common image formats
 */
export const IMAGE_FORMATS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'svg',
  'bmp',
  'ico',
  'tiff',
  'tif',
  'avif',
  'apng',
] as const;

/**
 * Common video formats
 */
export const VIDEO_FORMATS = [
  'mp4',
  'mov',
  'avi',
  'wmv',
  'flv',
  'webm',
  'mkv',
  'm4v',
  'mpg',
  'mpeg',
] as const;

/**
 * Common document formats
 */
export const DOCUMENT_FORMATS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'rtf',
  'odt',
  'ods',
  'odp',
] as const;

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || '';
}

/**
 * Check if file supports image presets/transformations
 */
export function supportsPresets(filename: string, mimetype?: string): boolean {
  const ext = getFileExtension(filename);
  const mimeStr = (mimetype || '').toLowerCase();
  
  return PRESET_SUPPORTED_FORMATS.some(
    (format) => ext === format || mimeStr.includes(format)
  );
}

/**
 * Check if file supports drag-and-drop embedding
 */
export function supportsDragDrop(filename: string): boolean {
  const ext = getFileExtension(filename);
  return DRAG_DROP_SUPPORTED_FORMATS.includes(ext as any);
}

/**
 * Check if file is an image
 */
export function isImageFile(filename: string): boolean {
  const ext = getFileExtension(filename);
  return IMAGE_FORMATS.includes(ext as any);
}

/**
 * Check if file is a video
 */
export function isVideoFile(filename: string): boolean {
  const ext = getFileExtension(filename);
  return VIDEO_FORMATS.includes(ext as any);
}

/**
 * Check if file is a document
 */
export function isDocumentFile(filename: string): boolean {
  const ext = getFileExtension(filename);
  return DOCUMENT_FORMATS.includes(ext as any);
}

/**
 * Get MIME type from filename
 */
export function getMimeType(filename: string): string {
  const ext = getFileExtension(filename);
  
  // Image types
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'svg') return 'image/svg+xml';
  if (ext === 'bmp') return 'image/bmp';
  if (ext === 'ico') return 'image/x-icon';
  if (ext === 'tiff' || ext === 'tif') return 'image/tiff';
  
  // Video types
  if (ext === 'mp4') return 'video/mp4';
  if (ext === 'mov') return 'video/quicktime';
  if (ext === 'avi') return 'video/x-msvideo';
  if (ext === 'webm') return 'video/webm';
  
  // Document types
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'doc') return 'application/msword';
  if (ext === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (ext === 'xls') return 'application/vnd.ms-excel';
  if (ext === 'xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (ext === 'ppt') return 'application/vnd.ms-powerpoint';
  if (ext === 'pptx') return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
  if (ext === 'txt') return 'text/plain';
  
  return 'application/octet-stream';
}

/**
 * Get file type icon name (Material Symbols)
 */
export function getFileTypeIcon(filename: string): string {
  if (isImageFile(filename)) return 'image';
  if (isVideoFile(filename)) return 'videocam';
  if (isDocumentFile(filename)) return 'description';
  
  const ext = getFileExtension(filename);
  
  // Audio
  if (['mp3', 'wav', 'ogg', 'flac', 'm4a'].includes(ext)) return 'audiotrack';
  
  // Archive
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'folder_zip';
  
  // Code
  if (['js', 'ts', 'jsx', 'tsx', 'html', 'css', 'json', 'xml'].includes(ext)) return 'code';
  
  return 'insert_drive_file';
}